import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight, FaFutbol, FaMapMarkerAlt, FaTrophy, FaInfoCircle, FaCamera } from 'react-icons/fa';
import '../scss/Stories.scss';
import api from '../services/apiService';
import confetti from 'canvas-confetti';
import { achievements as achievementsList } from '../data/achievements';

// No Stories Message component
const NoStoriesMessage = () => {
  return (
    <div className="story no-stories">
      <div className="no-stories-content">
        <FaInfoCircle className="info-icon" />
        <h2>No Stories Yet</h2>
        <p>Check back later for match results and achievements!</p>
      </div>
    </div>
  );
};

// Match Story component
const MatchStory = ({ match }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showScore, setShowScore] = useState(false);
  const [showWinner, setShowWinner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  
  // Ensure match data has all required fields
  const safeMatch = {
    match_id: match.match_id || 0,
    match_date: match.match_date || new Date().toISOString(),
    location: match.location || "Unknown Location",
    white_score: match.white_score || 0,
    color_score: match.color_score || 0,
    team_id: match.team_id || 1,
    status: match.status || "completed",
    scorers: match.scorers || [],
    motm_player_id: match.motm_player_id || null,
    player_name: match.player_name || "Unknown Player",
    team_name: match.team_name || "Unknown Team"
  };
  
  useEffect(() => {
    // Animation sequence
    const showContentTimer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    const showScoreTimer = setTimeout(() => {
      setShowScore(true);
    }, 1500);
    
    const showWinnerTimer = setTimeout(() => {
      setShowWinner(true);
      
      // Fire confetti for the winning team
      if (safeMatch.white_score !== safeMatch.color_score) {
        fireConfetti(safeMatch.white_score > safeMatch.color_score ? '#ffffff' : '#ff6b6b');
      }
    }, 3000);
    
    const showDetailsTimer = setTimeout(() => {
      setShowDetails(true);
    }, 4500);
    
    return () => {
      clearTimeout(showContentTimer);
      clearTimeout(showScoreTimer);
      clearTimeout(showWinnerTimer);
      clearTimeout(showDetailsTimer);
    };
  }, [safeMatch]);
  
  // Confetti for winning team
  const fireConfetti = (primaryColor) => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 1500
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
        scalar: 1.2,
        shapes: ['circle', 'square'],
        colors: [primaryColor, '#ffd700', '#ffffff'],
        ticks: 200,
        gravity: 1.2,
        decay: 0.94,
        startVelocity: 30,
        spread: 70,
      });
    }

    // Launch confetti
    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    });
    fire(0.2, {
      spread: 60,
    });
    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8
    });
  };
  
  // Determine winner
  const getWinnerText = () => {
    if (safeMatch.white_score > safeMatch.color_score) {
      return "White Team Wins!";
    } else if (safeMatch.color_score > safeMatch.white_score) {
      return "Color Team Wins!";
    } else {
      return "It's a Draw!";
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };
  
  // Render scorers if available
  const renderScorers = () => {
    if (!safeMatch.scorers || !Array.isArray(safeMatch.scorers) || safeMatch.scorers.length === 0) {
      return null;
    }
    
    const whiteScorers = safeMatch.scorers.filter(scorer => scorer.team === 'white');
    const colorScorers = safeMatch.scorers.filter(scorer => scorer.team === 'color');
    
    return (
      <div className={`match-scorers ${showDetails ? 'visible' : ''}`}>
        <h4>Goal Scorers</h4>
        <div className="scorers-container">
          <div className="team-scorers white-scorers">
            {whiteScorers.map((scorer, index) => (
              <div key={`white-${index}`} className="scorer">
                <FaFutbol className="goal-icon" />
                <span>Player {scorer.player_id} ({scorer.goals})</span>
              </div>
            ))}
          </div>
          <div className="team-scorers color-scorers">
            {colorScorers.map((scorer, index) => (
              <div key={`color-${index}`} className="scorer">
                <FaFutbol className="goal-icon" />
                <span>Player {scorer.player_id} ({scorer.goals})</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
          // Render POTM if available
  const renderMotm = () => {
    if (!safeMatch.motm_player_id) {
      return null;
    }
    
    return (
      <div className={`match-motm ${showDetails ? 'visible' : ''}`}>
        <h4>Man of the Match</h4>
        <div className="motm-container">
          <FaTrophy className="motm-icon" />
          <span>Player {safeMatch.motm_player_id}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="story match-story">
      <div className={`match-content ${isVisible ? 'visible' : ''}`}>
        <div className="match-header">
          <div className="team-info">
            <div className="team-name">{safeMatch.team_name}</div>
            {safeMatch.player_name && (
              <div className="player-name">Shared by {safeMatch.player_name}</div>
            )}
          </div>
          <div className="match-date">
            {formatDate(safeMatch.match_date)}
          </div>
          <div className="match-location">
            <FaMapMarkerAlt /> {safeMatch.location}
          </div>
        </div>
        
        <h2 className="match-title">Match Result</h2>
        
        <div className="match-score-display">
          <div className="score-team">
            <span className="score-number">{safeMatch.white_score}</span>
            <span className="team-label">White Team</span>
          </div>
          <span className="score-separator">-</span>
          <div className="score-team">
            <span className="score-number">{safeMatch.color_score}</span>
            <span className="team-label">Color Team</span>
          </div>
        </div>
        
        <div className={`teams-container ${showScore ? 'show-score' : ''}`}>
          <div className="team white-team">
            <div className="team-name">White Team</div>
            <div className="team-score">{safeMatch.white_score}</div>
          </div>
          
          <div className="versus">
            <FaFutbol className="ball-icon" />
          </div>
          
          <div className="team color-team">
            <div className="team-name">Color Team</div>
            <div className="team-score">{safeMatch.color_score}</div>
          </div>
        </div>
        
        {showWinner && (
          <div className="winner-announcement">
            <FaTrophy className="trophy-icon" />
            <h3>{getWinnerText()}</h3>
          </div>
        )}
        
        {renderScorers()}
        {renderMotm()}
      </div>
    </div>
  );
};

// Achievement Story component
const AchievementStory = ({ achievement }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Reset visibility state when achievement changes
    setIsVisible(false);
    
    // Short delay before showing content and firing confetti
    const timer = setTimeout(() => {
      setIsVisible(true);
      fireConfetti();
    }, 100);

    return () => clearTimeout(timer);
  }, [achievement.id]); // Re-run effect when achievement ID changes

  // Move confetti logic to a separate function
  const fireConfetti = () => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 1500
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
        scalar: 1.2,
        shapes: ['star', 'circle'],
        colors: ['#ffd700', '#ffa500', '#ffffff', '#ff6b6b'],
        ticks: 200,
        gravity: 1.2,
        decay: 0.94,
        startVelocity: 30,
        spread: 70,
      });
    }

    // Launch confetti
    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    });
    fire(0.2, {
      spread: 60,
    });
    fire(0.35, {
      spread: 100,
      decay: 0.91,
      scalar: 0.8
    });
  };

  // Find achievement details
  const achievementDetails = achievementsList.find(a => a.id === achievement.id) || {
    title: 'Achievement',
    description: 'Congratulations!',
    icon: null
  };

  return (
    <div className="story achievement-story">
      <div className={`achievement-content ${isVisible ? 'visible' : ''}`}>
        <div className="achievement-header">
          <div className="team-name">{achievement.team_name}</div>
          <div className="player-name">{achievement.player_name}</div>
          <div className="achievement-date">
            {new Date(achievement.created_at).toLocaleDateString()}
          </div>
        </div>
        <div className="achievement-icon">
          {achievementDetails.icon}
        </div>
        <h2>Achievement Unlocked!</h2>
        <h3>{achievementDetails.title}</h3>
        <p>{achievementDetails.description}</p>
      </div>
    </div>
  );
};

// Image Story component
const ImageStory = ({ story, storyId }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // Short delay before showing content
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    // Track view when story is loaded
    if (storyId) {
      trackStoryView(storyId);
    } else {
      console.error('Missing story ID for tracking view');
    }

    return () => clearTimeout(timer);
  }, [storyId || '']);

  // Track story view
  const trackStoryView = async (storyId) => {
    try {
      console.log('Tracking view for story:', storyId);
      
      // Extract numeric ID if it starts with img_
      if (typeof storyId === 'string' && storyId.startsWith('img_')) {
        storyId = storyId.replace('img_', '');
      }
      
      // Create form data for better compatibility
      const formData = new FormData();
      formData.append('story_id', storyId);
      formData.append('action', 'view');
      
      // Make the API call
      const response = await api.post(`${process.env.REACT_APP_API_URL}/track_story.php`, formData);
      console.log('View tracking response:', response.data);
    } catch (error) {
      console.error('Error tracking story view:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  // Track click on title or button
  const trackStoryClick = async (storyId, clickType) => {
    try {
      // Extract numeric ID if it starts with img_
      if (typeof storyId === 'string' && storyId.startsWith('img_')) {
        storyId = storyId.replace('img_', '');
      }
      
      console.log('Tracking click for story:', storyId, 'Type:', clickType);
      
      // Create form data for better compatibility
      const formData = new FormData();
      formData.append('story_id', storyId);
      formData.append('action', 'click');
      formData.append('click_type', clickType);
      
      // Make the API call
      const response = await api.post(`${process.env.REACT_APP_API_URL}/track_story.php`, formData);
      console.log('Click tracking response:', response.data);
    } catch (error) {
      console.error('Error tracking story click:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  // Handle title click
  const handleTitleClick = () => {
    if (story.button_url) {
      if (storyId) {
        trackStoryClick(storyId, 'title');
      } else {
        console.error('Missing story ID for tracking click');
      }
      window.open(story.button_url, '_blank');
    }
  };

  // Handle action button click
  const handleActionButtonClick = (e) => {
    e.stopPropagation(); // Prevent story navigation
    
    if (storyId) {
      trackStoryClick(storyId, 'action_button');
    } else {
      console.error('Missing story ID for tracking click');
    }
    
    // Open URL in new tab if provided
    if (story.button_url) {
      window.open(story.button_url, '_blank');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  // Default placeholder in case image is missing
  const imageUrl = story.image_url || '/images/stories/placeholder.jpg';

  return (
    <div className="story image-story">
      <div className={`image-content ${isVisible ? 'visible' : ''} ${imageLoaded ? 'image-loaded' : ''}`}>
        <div className="image-container">
          {!imageLoaded && (
            <div className="image-loading">
              <div className="loading-spinner"></div>
              <p>Loading image...</p>
            </div>
          )}
          <img 
            src={imageUrl} 
            alt={story.title || "Story image"} 
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              console.error("Error loading image:", e);
              e.target.src = '/images/stories/placeholder.jpg';
            }}
          />
        </div>
        
        {story.title && (
          <div className="image-title-container">
            <span className="ad-label">Ad</span>
            <h3 
              className="image-title clickable" 
              onClick={handleTitleClick}
            >
              {story.title}
            </h3>
          </div>
        )}
        {story.description && <p className="image-description">{story.description}</p>}
        
        {story.button_url && (
          <button 
            className="story-action-button"
            onClick={handleActionButtonClick}
          >
            {story.button_text || "Learn More"}
          </button>
        )}
      </div>
    </div>
  );
};

const Stories = ({ isOpen, onClose }) => {
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [progress, setProgress] = useState(0);
  const [stories, setStories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const progressInterval = useRef(null);
  const storyContainerRef = useRef(null);
  const storyDuration = 6000; // 6 seconds per story
  
  // Function to mark story as seen
  const markStorySeen = (storyId, storyType) => {
    const playerId = sessionStorage.getItem('player_id');
    const storageKey = `seen_${storyType}_${playerId}`;
    const seenStories = JSON.parse(localStorage.getItem(storageKey) || '[]');
    
    if (!seenStories.includes(storyId)) {
      seenStories.push(storyId);
      localStorage.setItem(storageKey, JSON.stringify(seenStories));
    }
  };

  // Function to check if story has been seen
  const hasBeenSeen = (storyId, storyType) => {
    const playerId = sessionStorage.getItem('player_id');
    const storageKey = `seen_${storyType}_${playerId}`;
    const seenStories = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return seenStories.includes(storyId);
  };

  // Fetch achievements and last match
  useEffect(() => {
    const fetchStories = async () => {
      setIsLoading(true);
      try {
        const playerId = sessionStorage.getItem('player_id');
        const teamId = sessionStorage.getItem('selected_team_id');
        
        if (!teamId) {
          console.error('No team ID found');
          setStories([]);
          setIsLoading(false);
          return;
        }
        
        console.log('Fetching stories for player:', playerId, 'team:', teamId);
        
        let allStories = [];
        
        // Fetch achievements and match data from team-specific endpoint
        try {
          const achievementsResponse = await api.get(`${process.env.REACT_APP_API_URL}/fetch_team_achievements.php?player_id=${playerId}&team_id=${teamId}`);
          console.log('Team achievements response:', achievementsResponse.data);
          
          // Add achievements and match notifications to stories
          if (achievementsResponse?.data?.success && 
              achievementsResponse?.data?.achievements && 
              Array.isArray(achievementsResponse.data.achievements) && 
              achievementsResponse.data.achievements.length > 0) {
            
            const formattedStories = achievementsResponse.data.achievements.map(achievement => {
              if (achievement.type === 'last_match' && achievement.match_data) {
                // Handle match notification
                return {
                  id: `match_${achievement.id}`,
                  type: 'match',
                  data: {
                    ...achievement.match_data,
                    player_name: achievement.player_name,
                    team_name: achievement.team_name,
                    created_at: achievement.created_at
                  }
                };
              } else {
                // Handle regular achievement
                return {
                  id: achievement.id,
                  type: 'achievement',
                  data: {
                    ...achievement,
                    created_at: achievement.created_at
                  }
                };
              }
            });
            
            allStories = [...allStories, ...formattedStories];
          }
        } catch (achievementError) {
          console.error('Error fetching achievements:', achievementError);
        }
        
        // Fetch image stories
        try {
          const imageStoriesResponse = await api.get(`${process.env.REACT_APP_API_URL}/fetch_image_stories.php?player_id=${playerId}&team_id=${teamId}`);
          console.log('Image stories response:', imageStoriesResponse.data);
          
          // Add image stories to the collection
          if (imageStoriesResponse?.data?.success && 
              imageStoriesResponse?.data?.stories && 
              Array.isArray(imageStoriesResponse.data.stories) && 
              imageStoriesResponse.data.stories.length > 0) {
            allStories = [...allStories, ...imageStoriesResponse.data.stories];
          }
        } catch (imageError) {
          console.error('Error fetching image stories:', imageError);
        }
        
        // Fetch last match from matches table as fallback if no match stories were found
        if (!allStories.some(story => story.type === 'match')) {
          try {
            const matchesResponse = await api.get(`${process.env.REACT_APP_API_URL}/get_matches.php?team_id=${teamId}&limit=1&status=completed`);
            console.log('Last match response:', matchesResponse.data);
            
            // Add last match to stories if available
            if (matchesResponse?.data?.success && 
                matchesResponse?.data?.matches && 
                Array.isArray(matchesResponse.data.matches) && 
                matchesResponse.data.matches.length > 0) {
              
              const lastMatchData = matchesResponse.data.matches[0];
              
              // Only add if the match has scores and belongs to the current team
              if (lastMatchData && 
                  lastMatchData.white_score !== undefined && 
                  lastMatchData.color_score !== undefined &&
                  lastMatchData.team_id === parseInt(teamId)) {
                
                allStories.push({
                  id: `match_${lastMatchData.match_id}`,
                  type: 'match',
                  data: lastMatchData
                });
              }
            }
          } catch (matchError) {
            console.error('Error fetching matches:', matchError);
          }
        }
        
        // Sort stories by creation date, newest first
        allStories.sort((a, b) => {
          const dateA = new Date(a.data.created_at);
          const dateB = new Date(b.data.created_at);
          return dateB - dateA;
        });
        
        console.log('All stories collected:', allStories);
        setStories(allStories);
      } catch (error) {
        console.error('Error fetching stories:', error);
        if (error.response) {
          console.error('Error response:', error.response.data);
        }
        // Set empty array to prevent further errors
        setStories([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchStories();
    }
  }, [isOpen]);

  // Reset progress when story index changes
  useEffect(() => {
    if (isOpen && stories.length > 0) {
      setProgress(0);
    }
  }, [currentStoryIndex, isOpen, stories.length]);

  // Mark current story as seen when it's viewed
  useEffect(() => {
    if (stories.length > 0 && currentStoryIndex >= 0 && isOpen) {
      // Only mark as seen, don't reset progress here
      const currentStory = stories[currentStoryIndex];
      // Use setTimeout to move the state update out of the render cycle
      setTimeout(() => {
        markStorySeen(currentStory.id, currentStory.type);
      }, 0);
    }
  }, [currentStoryIndex, stories, isOpen]);

  // Handle story auto-progression
  useEffect(() => {
    if (isOpen && stories.length > 0) {
      // Timer already reset in the dedicated effect above
      
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
      
      // Use a small delay before starting the timer to prevent rapid progression
      const timerDelay = setTimeout(() => {
        const intervalTime = 100;
        progressInterval.current = setInterval(() => {
          setProgress(prev => {
            const newProgress = prev + (intervalTime / storyDuration) * 100;
            
            if (newProgress >= 100) {
              // Clear the interval immediately to prevent multiple triggers
              clearInterval(progressInterval.current);
              progressInterval.current = null;
              
              // Use requestAnimationFrame to ensure UI updates before state change
              requestAnimationFrame(() => {
                if (currentStoryIndex < stories.length - 1) {
                  setCurrentStoryIndex(currentStoryIndex + 1);
                } else {
                  onClose();
                }
              });
              
              return 100; // Keep at 100% until the next story loads
            }
            return newProgress;
          });
        }, intervalTime);
      }, 200); // Short delay before starting timer
      
      return () => {
        clearTimeout(timerDelay);
        if (progressInterval.current) {
          clearInterval(progressInterval.current);
          progressInterval.current = null;
        }
      };
    }
  }, [isOpen, stories.length, currentStoryIndex, onClose, storyDuration]);

  // Handle touch events for swipe
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    setProgress(0);
    
    if (isLeftSwipe && currentStoryIndex < stories.length - 1) {
      setCurrentStoryIndex(currentStoryIndex + 1);
    } else if (isRightSwipe && currentStoryIndex > 0) {
      setCurrentStoryIndex(currentStoryIndex - 1);
    }
  };

  // Handle manual navigation
  const goToPrevStory = () => {
    if (currentStoryIndex > 0) {
      setProgress(0);
      setCurrentStoryIndex(currentStoryIndex - 1);
    }
  };

  const goToNextStory = () => {
    if (currentStoryIndex < stories.length - 1) {
      setProgress(0);
      setCurrentStoryIndex(currentStoryIndex + 1);
    } else {
      onClose();
    }
  };

  // Render the current story based on its type
  const renderCurrentStory = () => {
    if (isLoading) {
      return (
        <div className="story loading-story">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p>Loading stories...</p>
          </div>
        </div>
      );
    }
    
    if (stories.length === 0) {
      return <NoStoriesMessage />;
    }
    
    const currentStory = stories[currentStoryIndex];
    
    switch (currentStory.type) {
      case 'achievement':
        return (
          <AchievementStory 
            key={`achievement-${currentStory.id}-${currentStoryIndex}`} 
            achievement={currentStory.data} 
          />
        );
      case 'match':
        return (
          <MatchStory 
            key={`match-${currentStory.id}-${currentStoryIndex}`} 
            match={currentStory.data} 
          />
        );
      case 'image':
        return (
          <ImageStory 
            key={`image-${currentStory.id}-${currentStoryIndex}`} 
            story={currentStory.data}
            storyId={currentStory.id} 
          />
        );
      default:
        return <NoStoriesMessage />;
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="stories-container"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="stories-progress-bar">
        {stories.length > 0 ? (
          stories.map((story, index) => (
            <div 
              key={index} 
              className={`progress-segment ${hasBeenSeen(story.id, story.type) ? 'seen' : 'unseen'}`}
            >
              <div 
                className="progress-fill" 
                style={{ 
                  width: index === currentStoryIndex ? `${progress}%` : index < currentStoryIndex ? '100%' : '0%' 
                }}
              />
            </div>
          ))
        ) : (
          <div className="progress-segment">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
      
      <div className="stories-content">
        {renderCurrentStory()}
      </div>
      
      <button className="close-stories" onClick={onClose}>
        <FaTimes />
      </button>
      
      {stories.length > 1 && (
        <div className="stories-navigation">
          <button 
            className="nav-button prev" 
            onClick={goToPrevStory}
            disabled={currentStoryIndex === 0}
          >
            <FaChevronLeft />
          </button>
          <button 
            className="nav-button next" 
            onClick={goToNextStory}
          >
            <FaChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default Stories; 